import { bootstrapApplication } from '@angular/platform-browser';
import { environment } from '@env/environment';

/**
 * Multi-region runtime config (prod). ONE global build serves every pod: before the app boots we
 * fetch `/runtime-config.json` and, keyed by the current hostname, hydrate the `environment` object
 * in place with this pod's backend URLs + Cognito pool. Every existing consumer keeps reading
 * `environment.*` unchanged (it's the same singleton object).
 *
 * Safe + backward compatible:
 *  - File missing (404) or no entry for this host → `environment` keeps its build-time (sed-injected)
 *    values, so behaviour is identical to today. Non-prod hosts (app-dev/app-qa) need no entry.
 *  - Unsubstituted placeholder values (containing "__") are ignored, so a template the deploy
 *    pipeline hasn't filled can never inject broken values.
 *  - Runs BEFORE the app modules are imported, so DI factories (e.g. the Cognito AUTH_ADAPTER in
 *    app.config.ts), HTTP interceptors, and services all observe the hydrated values.
 *
 * The live `/runtime-config.json` is produced by the deploy pipeline (per-pod Cognito pool ids come
 * from secrets, exactly like the existing `environment` sed step). See public/runtime-config.example.json.
 */
type PodConfig = Record<string, unknown> & { cognito?: Record<string, unknown> };

/** Drop any field whose value is still an unsubstituted `__PLACEHOLDER__` token. */
function withoutPlaceholders<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => !(typeof v === 'string' && v.includes('__'))),
  ) as Partial<T>;
}

async function hydrateEnvironment(): Promise<void> {
  try {
    const res = await fetch('/runtime-config.json', { cache: 'no-store' });
    if (!res.ok) return;
    const cfg = (await res.json()) as { byHost?: Record<string, PodConfig>; default?: PodConfig };
    const pod = cfg.byHost?.[window.location.hostname] ?? cfg.default;
    if (!pod || typeof pod !== 'object') return;
    const { cognito, ...rest } = pod;
    Object.assign(environment, withoutPlaceholders(rest));
    if (cognito && typeof cognito === 'object') {
      Object.assign(environment.cognito, withoutPlaceholders(cognito));
    }
  } catch {
    /* no/invalid runtime config → keep build-time environment values (backward compatible) */
  }
}

async function main(): Promise<void> {
  await hydrateEnvironment();
  // Import the app graph only AFTER hydration so no module-load read captures stale config.
  const [{ appConfig }, { AppComponent }] = await Promise.all([
    import('./app/app.config'),
    import('./app/app'),
  ]);
  await bootstrapApplication(AppComponent, appConfig);
}

main().catch((err) => console.error(err));
